import React, { FC, useCallback } from 'react';
import styled from 'styled-components';
import { useDataTableFilterContext } from '../../utils/DataTableFilterContext';
import Typography from '../../../Typography/Typography';
import { MuiChip } from '../../../Chip/MuiChip';
import { Button } from '../../../Button';
import { DataTableSaveFilters } from './DataTableSaveFilters';
import { useIntlUds } from '../../../../translations/UdsIntlProvider';
import { DataTableFilterProps } from './DataTableFiltersChip';
import { FilterPicker } from './FilterPicker';
import { scrollToFilterByKey } from './scrollToFilterByKey';

interface Props {
  filters: DataTableFilterProps<string>[];
}

export const ActiveAndAvailableFilters: FC<React.PropsWithChildren<Props>> = ({ filters }) => {
  const { clearVisibleFilters, activeFilters, clearFilter, clearFilterAll } = useDataTableFilterContext();

  const { intl, format } = useIntlUds();

  const handleFilterDelete = useCallback(
    (id: string) => {
      // if onClear function is defined by an dev, we call it, otherwise we execute default clear function
      const onClearFn = filters.find((filter) => filter.id === id)?.onClear;
      if (onClearFn) {
        onClearFn();
        clearVisibleFilters([id]);
      } else {
        clearFilter(id);
      }
    },
    [clearFilter, clearVisibleFilters, filters]
  );

  const activeFiltersLength = activeFilters?.length ?? 0;
  const availableFilters = format(intl.DataTable.Filters.ActiveAndAvailable.availableFilters, {
    availableFiltersLength: ((filters?.length ?? 0) - activeFiltersLength).toString()
  });

  return (
    <>
      <StyledFiltersPanel $paddingBottom>
        <StyledFiltersHeader>
          <Typography variant="h4" className="drawer-title">
            {format(intl.DataTable.Filters.ActiveAndAvailable.activeFilters, {
              activeFiltersLength: activeFiltersLength.toString()
            })}
          </Typography>
          <div>
            <Button
              disabled={activeFiltersLength === 0}
              dataTest="clear-all-filters"
              variant="text"
              color="primary"
              onClick={clearFilterAll}
            >
              {intl.DataTable.Filters.ActiveAndAvailable.clearAllFilters}
            </Button>
            <DataTableSaveFilters />
          </div>
        </StyledFiltersHeader>
        <StyledActiveFiltersChips>
          {activeFilters?.map((filter) => (
            <MuiChip
              data-test={`active-filter-${filter.id}`}
              size="small"
              onClick={() => scrollToFilterByKey(filter.id, { delay: 0 })}
              onDelete={() => handleFilterDelete(filter.id)}
              key={filter.id}
              label={filter.label}
            />
          ))}
        </StyledActiveFiltersChips>
      </StyledFiltersPanel>
      <StyledFiltersPanel $paddingBottom={false}>
        <StyledFiltersHeader>
          <Typography dataTest="available-filters-count" variant="h4" className="drawer-title">
            {availableFilters}
          </Typography>
        </StyledFiltersHeader>
      </StyledFiltersPanel>
      <FilterPicker filters={filters} />
    </>
  );
};

const StyledActiveFiltersChips = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacingSize.s};
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
`;

const StyledFiltersPanel = styled.div<{ $paddingBottom: boolean }>`
  padding-bottom: ${({ theme, $paddingBottom }) => ($paddingBottom ? theme.spacingSize.xl : 0)};
`;

const StyledFiltersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
